import { z } from 'zod';

/**
 * Enum for valid message types
 */
export const MessageTypeEnum = z.enum([
  'projectConfig',
  'error',
  'tools',
  'userMessage',
  'previewUrl',
  'jobDone',
  'jobStarted',
  'jobPaused',
  'jobResumed',
  'general',
  'llmTokensUsed',
  'iconGenerated',
  'appBuild',
  'chatDecisionDialog',
  'blockRollback',
]);

/**
 * Schema for user message attachment in responses
 * Supports both legacy format (base64 content) and new format (public URL from cloud storage)
 */
export const UserMessageAttachmentSchema = z
  .object({
    // Legacy format (backward compatible) - for old messages
    content: z.string().optional().describe('BASE64 encoded content (legacy format)'),

    // New format (cloud storage) - for new messages
    url: z.string().optional().describe('Public URL for frontend display'),

    // Common fields
    mimeType: z.string().describe('MIME type of the attachment'),
    filename: z.string().describe('Original filename'),
  })
  .refine((data) => data.content || data.url, {
    message: 'Either content (legacy) or url (new format) must be provided',
  });

export const ProjectConfigSchema = z.object({
  message: z.string().describe('User friendly message'),
  projectName: z.string().describe('Project name'),
  packageName: z.string().describe('Package name'),
});

export const ErrorTypeEnum = z.enum(['compilation', 'technical', 'fatal', 'rollback', 'prompt']);

export const PromptErrorReasonEnum = z.enum(['prompt_too_long', 'other']);

export const RollbackDetailsSchema = z.object({
  originalCheckpointId: z.string().describe('Checkpoint ID before rollback attempt'),
  targetCheckpointId: z.string().describe('Checkpoint ID that was being rolled back to'),
  newCheckpointId: z.string().optional().describe('New checkpoint ID created after rollback (if already created)'),
});

export const ErrorSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('compilation'),
    message: z.string().describe('Content of the error message'),
  }),
  z.object({
    type: z.literal('technical'),
    message: z.string().describe('Content of the error message'),
  }),
  z.object({
    type: z.literal('fatal'),
    message: z.string().describe('Content of the error message'),
    jobId: z.string().describe('ID of the job that encountered the fatal error'),
  }),
  z.object({
    type: z.literal('rollback'),
    message: z.string().describe('Content of the error message'),
    rollbackDetails: RollbackDetailsSchema.describe('Details about the failed rollback operation'),
  }),
  z.object({
    type: z.literal('prompt'),
    message: z.string().describe('Content of the error message'),
    reason: PromptErrorReasonEnum.describe('Reason for the prompt error'),
  }),
]);

// User message schema for responses
export const UserMessageSchema = z.object({
  userId: z.string().describe('ID of the user sending the message'),
  message: z.string().describe('Message content'),
  attachments: z.array(UserMessageAttachmentSchema).optional().describe('List of file attachments'),
});

// Message type for general assistant messages
export const GeneralAssistantMessageTopicEnum = z.enum([
  'buildPlan',
  'followUp',
  'imageDescription',
  'buildingWebPreview',
  'fixingCompilationErrors',
  'changeRequestConfirmation',
  'changeSummary',
  'noGenerationReason',
  'rollback',
  'other',
]);
export const GeneralAssistantMessageSchema = z.object({
  topic: GeneralAssistantMessageTopicEnum.describe('Topic of the message'),
  message: z.array(z.string()).describe('Message from the general assistant'),
});

// Terminal message for job processing
export const JobDoneSchema = z.string().describe('ID of the job that was completed');
export const JobStartedSchema = z.string().describe('ID of the job that was started');
export const JobPausedSchema = z.string().describe('ID of the job that was paused due to interruption');
export const JobResumedSchema = z.string().describe('ID of the job that was resumed after interruption');

// Status enum for tool usage
export const ToolUseStatusEnum = z.enum(['waiting', 'processing', 'done', 'error', 'warning']);
export const ToolTypeEnum = z.enum(['text_editor', 'generic', 'database']);

// text_editor tool schema - discriminated by status
export const TextEditorChangeTypeEnum = z.enum(['new', 'modified', 'deleted', 'viewed', 'moved']);
export const TextEditorToolUseSchemaMoved = z.object({
  toolType: ToolTypeEnum.extract(['text_editor']),
  path: z.string().describe('Path of the file'),
  purpose: z.string().optional().describe('Purpose or description of the file'),
  changeType: TextEditorChangeTypeEnum.extract(['moved']),
  destination: z.string().describe('Destination path for the moved file'),
});
export const TextEditorToolUseSchemaOther = z.object({
  toolType: ToolTypeEnum.extract(['text_editor']),
  path: z.string().describe('Path of the file'),
  purpose: z.string().optional().describe('Purpose or description of the file'),
  changeType: TextEditorChangeTypeEnum.exclude(['moved']),
});
export const TextEditorToolUseSchema = z.union([TextEditorToolUseSchemaMoved, TextEditorToolUseSchemaOther]);

// code_analyzer tool schema - discriminated by status
export const GenericToolCategoryEnum = z.enum(['other', 'code', 'database', 'auth', 'storage']);
export const GenericToolUseSchema = z.object({
  toolType: ToolTypeEnum.extract(['generic']),
  toolName: z.string().describe('Friendly name of the tool'),
  category: GenericToolCategoryEnum.describe('Category of the tool'),
  params: z.string().optional().describe('Parameters of the tool'),
  purpose: z.string().optional().describe('Purpose or description of the file'),
});

// database tool schema
export const DatabaseToolOperationEnum = z.enum([
  'database_created',
  'schema_changed',
  'data_modified',
  'data_retrieved',
  'no',
]);
export const DatabaseToolUseSchema = z.object({
  toolType: ToolTypeEnum.extract(['database']),
  toolName: z.string().describe('Friendly name of the database tool'),
  description: z.string().optional().describe('Description of what the database operation does'),
  operation: DatabaseToolOperationEnum.describe('Type of database modification operation'),
});

// Unified schema for all tool types - union of tool-specific schemas
export const ToolUseSchema = z.union([TextEditorToolUseSchema, GenericToolUseSchema, DatabaseToolUseSchema]);

// Schema for tool uses map
export const ToolUsesSchema = z.record(
  z.string(), // Tool use id
  z.discriminatedUnion('status', [
    z.object({
      status: ToolUseStatusEnum.extract(['error']),
      error: z.string().min(1).describe('Error message when status is error'),
      toolUse: ToolUseSchema,
    }),
    z.object({
      status: ToolUseStatusEnum.extract(['warning']),
      warning: z.string().min(1).describe('Warning message when status is warning'),
      toolUse: ToolUseSchema,
    }),
    z.object({
      status: ToolUseStatusEnum.exclude(['error', 'warning']),
      toolUse: ToolUseSchema,
    }),
  ])
);

export const ToolsSchema = z
  .object({
    toolUses: ToolUsesSchema,
  })
  .describe('Tool uses');

// Web preview link
export const PreviewUrlStatusEnum = z.enum(['success', 'updating', 'notAvailable', 'error']);
export const PreviewUrlSchema = z.object({
  url: z.string().describe('URL of the preview'),
  status: PreviewUrlStatusEnum.describe('Status of the preview build'),
});

export const GenerationStageEnum = z.enum([
  'chat',
  'code_generation',
  'change_request',
  'code_generation_autofix',
  'change_request_autofix',
  'manual_fix',
]);

export const LlmTokensUsedSchema = z.object({
  modelName: z.string().describe('LLM model name'),
  inputTokens: z.number().describe('Input tokens reported by LLM'),
  outputTokens: z.number().describe('Output tokens reported by LLM'),
  cacheTokens: z
    .object({
      read: z.number().describe('Number of tokens read from cache'),
      writeShort: z.number().describe('Number of tokens written to short term cache'),
      writeLong: z.number().describe('Number of tokens written to long term cache'),
    })
    .optional()
    .describe('Cache related tokens reported by LLM'),
  stage: GenerationStageEnum.describe(
    'What stage it happened; Initial generation prompts might be priced differently than later fixes'
  ),
});

export const IconGeneratedSchema = z.object({
  model: z.string().describe('Model used to generate icon'),
  stage: z
    .string()
    .describe('What stage it happened; Initial generation prompts might be priced differently than later fixes'),
});

export const AppBuildSchema = z.object({
  os: z.enum(['android', 'ios']),
});

export const ChatDecisionDialogSchema = z.object({
  title: z.string().describe('Dialog title'),
  message: z.string().describe('Dialog message'),
  options: z.array(
    z.object({
      label: z.string().describe('Option label'),
      description: z.string().describe('Option description'),
      value: z.string().describe('Value associated with this option'),
    })
  ),
});

// TODO(ilawicki): 'databaseCreated', 'databaseChange' are lest out for older projects on dev
export const BlockRollbackSchema = z
  .object({
    reason: z
      .enum(['databaseCreated', 'databaseChange', 'database_created', 'schema_changed', 'data_modified', 'no'])
      .default('databaseChange'),
  })
  .describe('Block rollback reason');

// Create TypeScript types based on the schemas
export type MessageType = z.infer<typeof MessageTypeEnum>;
export type ErrorType = z.infer<typeof ErrorTypeEnum>;
export type PromptErrorReason = z.infer<typeof PromptErrorReasonEnum>;
export type ProjectConfig = z.infer<typeof ProjectConfigSchema>;
export type ErrorContent = z.infer<typeof ErrorSchema>;
export type UserMessageAttachment = z.infer<typeof UserMessageAttachmentSchema>;
export type UserMessage = z.infer<typeof UserMessageSchema>;
export type JobDone = z.infer<typeof JobDoneSchema>;
export type JobStarted = z.infer<typeof JobStartedSchema>;
export type JobPaused = z.infer<typeof JobPausedSchema>;
export type JobResumed = z.infer<typeof JobResumedSchema>;
export type PreviewUrl = z.infer<typeof PreviewUrlSchema>;
export type PreviewUrlStatus = z.infer<typeof PreviewUrlStatusEnum>;
export type LlmTokensUsed = z.infer<typeof LlmTokensUsedSchema>;
export type IconGenerated = z.infer<typeof IconGeneratedSchema>;
export type AppBuild = z.infer<typeof AppBuildSchema>;
export type GeneralAssistantMessage = z.infer<typeof GeneralAssistantMessageSchema>;
export type GenerationStage = z.infer<typeof GenerationStageEnum>;
export type ChatDecisionDialog = z.infer<typeof ChatDecisionDialogSchema>;
export type BlockRollback = z.infer<typeof BlockRollbackSchema>;
export type Tools = z.infer<typeof ToolsSchema>;
export type ToolUses = z.infer<typeof ToolUsesSchema>;
export type ToolType = z.infer<typeof ToolTypeEnum>;
export type ToolUse = z.infer<typeof ToolUseSchema>;
export type ToolUseStatus = z.infer<typeof ToolUseStatusEnum>;
export type TextEditorChangeType = z.infer<typeof TextEditorChangeTypeEnum>;
export type TextEditorToolUse = z.infer<typeof TextEditorToolUseSchema>;
export type GenericToolUse = z.infer<typeof GenericToolUseSchema>;
export type GenericToolCategory = z.infer<typeof GenericToolCategoryEnum>;
export type DatabaseToolOperation = z.infer<typeof DatabaseToolOperationEnum>;
export type DatabaseToolUse = z.infer<typeof DatabaseToolUseSchema>;
/**
 * Gets the appropriate schema for a given message type
 * @param type The message type
 * @returns The Zod schema for validating content of the specified type
 */
export function getSchemaForType(type: MessageType): z.ZodTypeAny {
  switch (type) {
    case 'projectConfig':
      return ProjectConfigSchema;
    case 'error':
      return ErrorSchema;
    case 'userMessage':
      return UserMessageSchema;
    case 'jobDone':
      return JobDoneSchema;
    case 'jobStarted':
      return JobStartedSchema;
    case 'jobPaused':
      return JobPausedSchema;
    case 'jobResumed':
      return JobResumedSchema;
    case 'tools':
      return ToolsSchema;
    case 'previewUrl':
      return PreviewUrlSchema;
    case 'general':
      return GeneralAssistantMessageSchema;
    case 'llmTokensUsed':
      return LlmTokensUsedSchema;
    case 'iconGenerated':
      return IconGeneratedSchema;
    case 'appBuild':
      return AppBuildSchema;
    case 'chatDecisionDialog':
      return ChatDecisionDialogSchema;
    case 'blockRollback':
      return BlockRollbackSchema;
    default:
      throw new Error(`Unknown message type: ${type}`);
  }
}
